import { Vue, Component, State, namespace, Prop } from 'nuxt-property-decorator'
import isEmpty from 'lodash/isEmpty'
import cloneDeep from 'lodash/cloneDeep'
import CategoryTree from '~/components/experience/destinations-category-tree/category-tree.vue'
import RangesTree from '~/components/experience/destinations-category-tree/ranges-tree.vue'
import ModalTree from '~/components/experience/destinations-category-tree/modal-tree.vue'
import { Experience } from '~/types/experience'
import eventBus from '~/pages/experience/pay/common/event-bus'

import UrlQuery = Experience.UrlQuery
import TreeNode = Experience.TreeNode

const experience = namespace('experience')

@Component({
  components: {
    CategoryTree,
    RangesTree,
    ModalTree
  }
})
export default class TreeModel extends Vue {
  @State klook!: Data.Klook
  @experience.Mutation('SET_URL_QUERY_PAYLOAD') setUrlQueryPayload!: Function
  @experience.Mutation('CLEAR_FILTER_CONDITIONS') clearFilterConditions!: Function
  @experience.Mutation('SET_INIT_CATEGORY_ID') setInitCategoryId!: Function
  @experience.Action setUrlQuery!: Function
  @experience.Action setSelectedCategory!: Function
  @experience.Action setSelectedRanges!: Function
  @experience.State urlQuery!: UrlQuery
  @experience.State pageType!: string
  @experience.State filter!: any
  @experience.State filterConditions!: any
  @experience.State categoryTreeData!: any

  @Prop({ default: false }) isAbExperiment!: boolean
  @Prop() type!: 'range' | 'category'
  @Prop() title!: string

  visible: boolean = false
  selectedRange: any = []
  selectedCategory: any = []
  clearDisable: boolean = true
  hasChange: boolean = false
  showSearchLayout: boolean = false
  cacheRangeData: any = null
  tempRangeData: any = null
  originRangeData: any = null // 进入页面获取到的range数据
  clearAllFlag: boolean = false //  半选状态

  get spmModule() {
    const o = {
      country_id: this.urlQuery.country_ids,
      city_id: this.urlQuery.city_ids
    }
    const category = this.urlQuery.frontend_id_list || ''
    const spm =
      this.type === 'range'
        ? `ConfirmDestination?trg=manual&ext=${JSON.stringify(o)}`
        : `ConfirmCategory?trg=manual&ext=${JSON.stringify({
            categorytree_id: category
          })}`
    return spm
  }

  getCategoryDefaultValue() {
    const target = (this.filter?.components || []).find((item: any) => item.key === 'frontend_id_list')
    if (target) {
      return target.data.default_key
    }

    return ''
  }

  closeModal() {
    this.visible = false
    const t = setTimeout(() => {
      this.$emit('close')
      clearTimeout(t)
    }, 300)
  }

  rangeChange(data: any, cacheData: any) {
    this.hasChange = true
    this.selectedRange = data
    this.tempRangeData = cloneDeep(cacheData)
    this.initClearDisableStatus()
  }

  categoryChange(data: any) {
    this.hasChange = true
    this.selectedCategory = data
    this.initClearDisableStatus()
  }

  formatRange() {
    const range = Object.keys(this.selectedRange.rangeId || []).join(',')
    const country = Object.keys(this.selectedRange.countryId || []).join(',')
    const city = Object.keys(this.selectedRange.cityId || []).join(',')
    return {
      range,
      country,
      city
    }
  }

  formatCategory() {
    return Object.keys(this.selectedCategory || []).join(',')
  }

  beforeConfirm() {
    if (this.type === 'range') {
      this.setSelectedRanges(JSON.parse(JSON.stringify(this.selectedRange)))
      const d = this.formatRange()

      return {
        range_id: d.range,
        country_ids: d.country,
        city_ids: d.city,
        page: 1
      }
    } else if (this.type === 'category') {
      this.setSelectedCategory(
        JSON.parse(JSON.stringify(this.selectedCategory))
      )
      return {
        frontend_id_list: this.formatCategory(),
        page: 1
      }
    }
  }

  onConfirm() {
    if (this.hasChange) {
      const p = this.beforeConfirm()
      if (this.isAbExperiment) {
        this.setUrlQueryPayload({
          start_date: '',
          frontend_id_list: this.getCategoryDefaultValue()
        })
        this.clearFilterConditions()
        this.cacheRangeData = cloneDeep(this.tempRangeData)
        this.tempRangeData = null
        eventBus.$emit('changeDestination')
      }
      this.setUrlQuery(p)
    }
    this.closeModal()
  }

  // 修改clear按钮的状态
  initClearDisableStatus() {
    if (this.type === 'range') {
      const range = this.formatRange()
      const r = range.city + range.country
      this.clearDisable = !r
    }
    if (this.type === 'category') {
      const category = this.formatCategory()
      this.clearDisable = !category
    }
  }

  /**
   * 校验目的地内城市勾选的数量和目的地长度是否相等，如果相等，右上角checkbox需要勾选
   * 注意：category也适用
   * **/

  validateNeedSelectCheckbox(list: TreeNode[]): boolean {
    return (list || []).every((_item: TreeNode) => _item.checked)
  }

  checkChildSelect(list: TreeNode[]) {
    return (list || []).some((_item: TreeNode) => _item.checked)
  }

  /**
 * 后端返回的数据没有保留country节点的勾选情况，
 * 进入页面需要根据country 子节点的勾选情况订正该country节点的勾选情况，反推country节点的勾选情况
 * 注意：category也适用
 * **/
  fixParentNodeChecked(data: TreeNode[], type: 'category' | 'range'): void {
    if (type === 'range') {
      data.forEach((_item: TreeNode) => {
        ;(_item.children || []).forEach((child: TreeNode) => {
          if (child?.children?.length) {
            const flag: boolean = this.validateNeedSelectCheckbox(child.children)
            flag && (this.$set(child, 'checked', true))
          }
        })
      })
    } else {
      data.forEach((_item: TreeNode) => {
        if (_item?.children) {
          const flag: boolean = this.validateNeedSelectCheckbox(_item.children)
          flag && (this.$set(_item, 'checked', true))
        }
      })
    }
  }

  // 数据扁平化为对象，便于直接通过id查找到操作的节点
  flatChildrenLevel(data: TreeNode[]) {
    let obj: any = {}
    data.forEach((item: TreeNode) => {
      const { children, id } = item
      obj[id] = item
      if (children?.length) {
        children.forEach((child: TreeNode) => {
          const { id, children } = child
          obj[id] = child
          let res: any = {}
          children && children.length && (res = this.flatChildrenLevel(children))
          if (!isEmpty(res)) {
            obj = { ...obj, ...res }
          }
        })
      }
    })
    return obj
  }

  mounted() {
    this.visible = true
    if (this.type === 'range') {
      const city = this.urlQuery.city_ids || ''
      const country = this.urlQuery.country_ids || ''
      this.clearDisable = !(city + country)
    } else {
      const category = this.urlQuery.frontend_id_list || ''
      this.clearDisable = !category
    }
  }
}
